import type { OverrideOptionInput } from '@erp/rpc';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import type {
  BulkOverrideForm,
  BulkOverrideLineResult,
  FulfillmentCandidatesSnapshot,
} from './bulkFulfillmentOverride';
import { bulkQuantityForLine } from './bulkQuantityForLine';
import { isReleasedStatus } from './releasedStatus';
import { vendorBuyOverrideInput } from './vendorCandidateRows';

function lineSkipReason(line: AssignmentLine): string | null {
  if (line.status === 'rejected') return 'trashed';
  if (isReleasedStatus(line.status) || line.approvalState === 'released') {
    return 'released';
  }
  const sku = line.productSku;
  if (sku == null || sku === '') return 'no_product';
  return null;
}

function buySkipReason(
  form: BulkOverrideForm,
  candidates: FulfillmentCandidatesSnapshot | undefined,
): string | null {
  const isBuy = form.action === 'buy' || form.action === 'buy_and_transfer';
  if (!isBuy) return null;
  const vendor = candidates?.vendors.find(
    (v) => v.supplierId === form.vendorId,
  );
  return vendor == null ? 'vendor_unavailable' : null;
}

function transferSkipReason(
  form: BulkOverrideForm,
  candidates: FulfillmentCandidatesSnapshot | undefined,
): string | null {
  const isTransfer =
    form.action === 'transfer' || form.action === 'buy_and_transfer';
  if (!isTransfer) return null;
  const source = candidates?.transferSources.find(
    (s) => s.warehouseId === form.sourceId,
  );
  return source == null ? 'source_unavailable' : null;
}

/**
 * A substitute or make line's quantity is another product's (the supplying
 * product, a component), so it cannot carry over to an override of the
 * line's product.
 */
function planKindSkipReason(
  line: AssignmentLine,
  form: BulkOverrideForm,
): string | null {
  const planKind = line.topOption?.planKind ?? 'direct';
  if (planKind === 'direct' || form.applyQuantityToAllLines) return null;
  return planKind === 'make' ? 'make_quantity' : 'substitute_quantity';
}

export function planOverrideInputForLine(
  line: AssignmentLine,
  form: BulkOverrideForm,
  candidates: FulfillmentCandidatesSnapshot | undefined,
): BulkOverrideLineResult {
  const skip =
    lineSkipReason(line) ??
    buySkipReason(form, candidates) ??
    transferSkipReason(form, candidates) ??
    planKindSkipReason(line, form);
  if (skip != null) {
    return { suggestionId: line.suggestionId, status: 'skipped', reason: skip };
  }

  const isBuy = form.action === 'buy' || form.action === 'buy_and_transfer';
  const isTransfer =
    form.action === 'transfer' || form.action === 'buy_and_transfer';
  const selectedVendor = candidates?.vendors.find(
    (v) => v.supplierId === form.vendorId,
  );
  const lineQuantity = bulkQuantityForLine(line, form);

  // A plain buy from a vendor that can't ship to the destination goes to its
  // pivot and transfers on from there (GEN-3246).
  if (form.action === 'buy' && selectedVendor != null) {
    return {
      suggestionId: line.suggestionId,
      status: 'applied',
      input: vendorBuyOverrideInput(line, selectedVendor, lineQuantity),
    };
  }

  const input: OverrideOptionInput = {
    suggestionId: line.suggestionId,
    action: form.action,
    supplierId: isBuy ? form.vendorId : null,
    shipToWarehouseId: isBuy ? line.destinationWarehouseId : null,
    buyQuantity: isBuy ? lineQuantity : null,
    unitCost: isBuy ? (selectedVendor?.unitPrice ?? null) : null,
    sourceWarehouseId: isTransfer ? form.sourceId : null,
    transferQuantity: isTransfer ? lineQuantity : null,
    setChosen: true,
  };

  return { suggestionId: line.suggestionId, status: 'applied', input };
}
